import React from 'react';
import SectionHeader from './SectionHeader';
import NotesInput from './NotesInput';
import LifeEventCheckbox from './LifeEventCheckbox';
import CurrencyInput from './CurrencyInput';

const EMPTY_JOB = {
  person: '',
  title: '',
  employer: '',
  employer_address: '',
  annual_salary: null,
  annual_bonus: '',
};
const EMPTY_COMP = { person: '', details: '' };

/**
 * Section 3 — Career & Income updates.
 *
 * Covers job changes, promotions, business activity, and retirement.
 */
export default function StepCareerIncome({ data, onChange }) {
  const value = data || {};

  function update(field, val) {
    const next = { ...value, [field]: val };
    if (field !== 'none_of_the_above' && val) {
      next.none_of_the_above = false;
    }
    onChange(next);
  }

  function setNone(checked) {
    if (checked) {
      onChange({ none_of_the_above: true, notes: value.notes });
    } else {
      onChange({ ...value, none_of_the_above: false });
    }
  }

  // Job changes list
  const jobs = (value.job_changes && value.job_changes.length > 0)
    ? value.job_changes
    : [{ ...EMPTY_JOB }];

  function updateJob(idx, field, val) {
    const next = jobs.map((j, i) => (i === idx ? { ...j, [field]: val } : j));
    update('job_changes', next);
  }

  function addJob() {
    update('job_changes', [...jobs, { ...EMPTY_JOB }]);
  }

  function removeJob(idx) {
    const next = jobs.filter((_, i) => i !== idx);
    update('job_changes', next.length > 0 ? next : [{ ...EMPTY_JOB }]);
  }

  // Promotions list
  const promos = (value.promotions && value.promotions.length > 0)
    ? value.promotions
    : [{ ...EMPTY_COMP }];

  function updatePromo(idx, field, val) {
    const next = promos.map((p, i) => (i === idx ? { ...p, [field]: val } : p));
    update('promotions', next);
  }

  function addPromo() {
    update('promotions', [...promos, { ...EMPTY_COMP }]);
  }

  function removePromo(idx) {
    const next = promos.filter((_, i) => i !== idx);
    update('promotions', next.length > 0 ? next : [{ ...EMPTY_COMP }]);
  }

  return (
    <div>
      <SectionHeader
        number={3}
        title="Situation Updates — Career &amp; Income"
        tip="Check any career or income changes since we last met. We'll
          ask for a few quick details where applicable."
        showAnnualNote={false}
      />

      <LifeEventCheckbox
        id="changed_jobs"
        label="Changed jobs / promotion"
        checked={value.changed_jobs}
        onChange={(c) => update('changed_jobs', c)}
      >
        {jobs.map((j, idx) => (
          <div className="row g-2 mb-2 position-relative" key={idx}>
            {idx > 0 && (
              <div className="col-12 d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-link btn-sm text-danger p-0"
                  onClick={() => removeJob(idx)}
                  aria-label={`Remove job change ${idx + 1}`}
                >
                  <i className="bi bi-x-lg me-1" />
                  Remove
                </button>
              </div>
            )}
            <div className="col-md-6">
              <label className="form-label">{idx === 0 ? 'Who has the new job/promotion *' : `Person (#${idx + 1})`}</label>
              <input
                type="text"
                className="form-control"
                value={j.person || ''}
                onChange={(e) => updateJob(idx, 'person', e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">{idx === 0 ? 'New job title *' : 'New job title'}</label>
              <input
                type="text"
                className="form-control"
                value={j.title || ''}
                onChange={(e) => updateJob(idx, 'title', e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">{idx === 0 ? 'Employer *' : 'Employer'}</label>
              <input
                type="text"
                className="form-control"
                value={j.employer || ''}
                onChange={(e) => updateJob(idx, 'employer', e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Employer address</label>
              <input
                type="text"
                className="form-control"
                value={j.employer_address || ''}
                onChange={(e) => updateJob(idx, 'employer_address', e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <CurrencyInput
                label={`Estimated annual salary${idx === 0 ? ' *' : ''}`}
                id={`job_salary_${idx}`}
                value={j.annual_salary}
                onChange={(v) => updateJob(idx, 'annual_salary', v)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Additional annual bonus</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. $25,000 or 15%"
                value={j.annual_bonus || ''}
                onChange={(e) => updateJob(idx, 'annual_bonus', e.target.value)}
              />
            </div>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm mb-2"
          onClick={addJob}
        >
          <i className="bi bi-plus-lg me-1" />
          Add another job change
        </button>
        <div className="form-text small text-muted">
          You'll be able to upload your handbook, benefits elections,
          recent paystub, and any new retirement statement on the final
          step.
        </div>
      </LifeEventCheckbox>

      <LifeEventCheckbox
        id="promoted"
        label="Change in compensation (bonus, equity, stock options)"
        checked={value.promoted}
        onChange={(c) => update('promoted', c)}
      >
        {promos.map((p, idx) => (
          <div className="row g-2 mb-2 position-relative" key={idx}>
            {idx > 0 && (
              <div className="col-12 d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-link btn-sm text-danger p-0"
                  onClick={() => removePromo(idx)}
                  aria-label={`Remove compensation change ${idx + 1}`}
                >
                  <i className="bi bi-x-lg me-1" />
                  Remove
                </button>
              </div>
            )}
            <div className="col-md-6">
              <label className="form-label">{idx === 0 ? 'Who *' : 'Person'}</label>
              <input
                type="text"
                className="form-control"
                value={p.person || ''}
                onChange={(e) => updatePromo(idx, 'person', e.target.value)}
              />
            </div>
            <div className="col-md-12">
              <label className="form-label">Details</label>
              <textarea
                className="form-control"
                rows={2}
                value={p.details || ''}
                onChange={(e) => updatePromo(idx, 'details', e.target.value)}
              />
            </div>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm mb-2"
          onClick={addPromo}
        >
          <i className="bi bi-plus-lg me-1" />
          Add another compensation change
        </button>
      </LifeEventCheckbox>

      <LifeEventCheckbox
        id="started_or_sold_business"
        label="Started or sold a business"
        checked={value.started_or_sold_business}
        onChange={(c) => update('started_or_sold_business', c)}
      >
        <label className="form-label">Details</label>
        <textarea
          className="form-control"
          rows={2}
          value={value.started_or_sold_business_details || ''}
          onChange={(e) =>
            update('started_or_sold_business_details', e.target.value)
          }
        />
      </LifeEventCheckbox>

      <LifeEventCheckbox
        id="retired"
        label="Retired or planning to retire soon"
        checked={value.retired}
        onChange={(c) => update('retired', c)}
      >
        <label className="form-label">Details</label>
        <textarea
          className="form-control"
          rows={2}
          placeholder="Anticipated date, retiree package details, etc."
          value={value.retired_details || ''}
          onChange={(e) => update('retired_details', e.target.value)}
        />
      </LifeEventCheckbox>

      <div className="form-check mt-3">
        <input
          className="form-check-input"
          type="checkbox"
          id="career_none"
          checked={!!value.none_of_the_above}
          onChange={(e) => setNone(e.target.checked)}
        />
        <label className="form-check-label fw-semibold" htmlFor="career_none">
          None of the above has changed since we last met.
        </label>
      </div>

      <NotesInput
        value={value.notes}
        onChange={(v) => update('notes', v)}
      />
    </div>
  );
}
